import React, { useEffect, useCallback, useMemo, useState } from 'react'
import { useScreen } from '@core/providers/ScreenContext'
import Text from 'antd/lib/typography/Text'
import { Icon } from '@components/molecules'
import {
  Header as HeaderWrapper,
  Wrapper,
  Sider,
  Workspace,
  SidebarBody,
  SidebarToggle,
  SiderNavigation,
  Content,
  SiderDrawer,
  NoAccessWrapper
} from './ManagementLayout.styled'
import { withConfigTheme } from '@core/hocs'
import Header from '@components/organisms/HeaderMenu/Header'
import { useStore } from '@core/hooks'
import { useTranslation } from 'react-i18next'
import { Image, Tooltip } from 'antd'
import { ADMIN_SETTING_PERMISSION_CODES } from '@constants/perm'
import { hasTokenPermission } from '@core/utils/permission'

const pageLocale = 'pages.management.EPayslipPage'

const ADMIN_ROUTE_PERMISSION_MAP = {
  login: ADMIN_SETTING_PERMISSION_CODES.LOGIN,
  'e-payslip': ADMIN_SETTING_PERMISSION_CODES.EPAYSLIP,
  'e-certificate': ADMIN_SETTING_PERMISSION_CODES.ECERTIFICATE,
  'e-leave': ADMIN_SETTING_PERMISSION_CODES.ELEAVE,
  'e-time': ADMIN_SETTING_PERMISSION_CODES.ETIME,
  portal: ADMIN_SETTING_PERMISSION_CODES.HOME,
  'pay-period': ADMIN_SETTING_PERMISSION_CODES.PAY_PERIOD,
  'workflow-setting': ADMIN_SETTING_PERMISSION_CODES.WORKFLOW,
  'audit-log': ADMIN_SETTING_PERMISSION_CODES.AUDIT_LOG,
  perm: ADMIN_SETTING_PERMISSION_CODES.PSEUDO_PERM
}

const ADMIN_MENU_ICONS = {
  login: 'Login',
  'e-payslip': 'Printer',
  'e-certificate': 'Attach',
  'e-leave': 'Bag',
  'e-time': 'Task',
  portal: 'Layout',
  'pay-period': 'Card',
  'workflow-setting': 'Group',
  'audit-log': 'News',
  perm: 'Gear'
}

const getCurrentAdminPath = (match) => {
  const currentPath = match?.url?.split('/')?.[3]

  return currentPath || 'login'
}

const getCurrentAdminMenuId = (path) => path

const ManagementLayout = ({
  match,
  children,
  configTheme,
  userData,
  permission
}) => {
  const { t } = useTranslation()
  const [drawerOpen, setDrawerOpen] = React.useState(false)
  const { xs, sm } = useScreen()
  const isMobile = xs || sm
  const [collapsed, setCollapsed] = useState(false)
  const [{ config }] = useStore()
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false)
  }, [isMobile])

  const buildMenuHeader = useCallback(
    (id) => ({
      id,
      title: t(`${pageLocale}.menus.${id}`),
      type: 'header'
    }),
    [t]
  )

  const buildMenuItem = useCallback(
    (id, titleKey = id) => ({
      id,
      title: t(`${pageLocale}.menus.${titleKey}`),
      type: 'item',
      permCode: ADMIN_ROUTE_PERMISSION_MAP[id]
    }),
    [t]
  )

  const canAccessMenuItem = useCallback(
    ({ permCode }) => {
      if (permission === undefined) {
        return true
      }

      return hasTokenPermission(permission, permCode)
    },
    [permission]
  )

  const filterMenuWithHeaders = useCallback(
    (menu) => {
      const filteredMenu = menu.filter(
        (item) => item.type === 'header' || canAccessMenuItem(item)
      )

      return filteredMenu.filter((item, index) => {
        if (item.type !== 'header') {
          return true
        }

        const nextHeaderIndex = filteredMenu.findIndex(
          (nextItem, nextIndex) =>
            nextIndex > index && nextItem.type === 'header'
        )
        const itemsUntilNextHeader = filteredMenu.slice(
          index + 1,
          nextHeaderIndex === -1 ? filteredMenu.length : nextHeaderIndex
        )

        return itemsUntilNextHeader.some((nextItem) => nextItem.type === 'item')
      })
    },
    [canAccessMenuItem]
  )

  const siderMenu = useMemo(() => {
    const rawMenu = [
      buildMenuHeader('implement-setting'),
      buildMenuItem('login'),
      buildMenuItem('e-payslip'),
      buildMenuItem('e-certificate'),
      buildMenuItem('e-leave'),
      buildMenuItem('e-time'),
      buildMenuItem('portal'),
      buildMenuItem('pay-period'),
      buildMenuItem('workflow-setting'),
      buildMenuItem('audit-log'),
      buildMenuItem('perm')
    ]

    return filterMenuWithHeaders(rawMenu)
  }, [buildMenuHeader, buildMenuItem, filterMenuWithHeaders])

  const currentAdminPath = getCurrentAdminPath(match)
  const currentAdminMenuId = getCurrentAdminMenuId(currentAdminPath)
  const currentAdminPermCode = ADMIN_ROUTE_PERMISSION_MAP[currentAdminPath]
  const isPermissionLoading = permission === undefined
  const hasCurrentPageAccess =
    !currentAdminPermCode ||
    hasTokenPermission(permission, currentAdminPermCode)

  const renderSider = (compact = false) => (
    <SidebarBody
      configTheme={configTheme}
      data-compact={compact}
      data-drawer={isMobile}
    >
      <div className="sidebar-brand">
        <Image
          width={36}
          alt="Logo"
          src={config?.mainSetting?.logoUrl || '/general-outsoucing-logo.png'}
        />
        <span className="sidebar-label" aria-hidden={compact}>
          ESS Workspace
        </span>
      </div>
      <div className="sidebar-section-heading" aria-hidden={compact}>
        <span className="sidebar-label">
          {t(`${pageLocale}.menus.implement-setting`)}
        </span>
      </div>
      <nav aria-label={t(`${pageLocale}.menus.implement-setting`)}>
        {siderMenu.map(({ id, title, type }) =>
          type === 'header' ? null : (
            <Tooltip key={id} title={compact ? title : null} placement="right">
              <SiderNavigation
                active={currentAdminMenuId === id}
                aria-current={currentAdminMenuId === id ? 'page' : undefined}
                aria-label={title}
                to={`/${match?.params?.companyGroup}/self-service/${id}`}
                onClick={() => {
                  if (isMobile) setDrawerOpen(false)
                }}
              >
                <span className="sidebar-icon" aria-hidden="true">
                  <Icon
                    icon={ADMIN_MENU_ICONS[id]}
                    color="currentColor"
                    size={20}
                  />
                </span>
                <span className="sidebar-label" aria-hidden={compact}>
                  {title}
                </span>
              </SiderNavigation>
            </Tooltip>
          )
        )}
      </nav>
      <footer className="sidebar-copyright">
        <div>
          <span>© {new Date().getFullYear()}</span> <span>GO-HRM</span>
        </div>
        <div>v0.1.0</div>
      </footer>
    </SidebarBody>
  )
  const toggleLabel = t(
    `${pageLocale}.navigation.${
      isMobile ? 'open' : collapsed ? 'expand' : 'collapse'
    }`
  )

  return (
    <Wrapper configTheme={configTheme}>
      <Sider
        width={250}
        collapsedWidth={76}
        collapsed={collapsed}
        trigger={null}
        id="admin-sidebar"
      >
        {renderSider(collapsed)}
      </Sider>
      <Workspace>
        <HeaderWrapper configTheme={configTheme}>
          <Header
            isAdmin
            match={match}
            userData={userData}
            headerForeground="var(--admin-header-foreground)"
            navigationToggle={
              <Tooltip title={toggleLabel}>
                <SidebarToggle
                  type="button"
                  className="admin-sidebar-toggle"
                  aria-label={toggleLabel}
                  aria-expanded={isMobile ? drawerOpen : !collapsed}
                  aria-controls={
                    isMobile ? 'admin-sidebar-drawer' : 'admin-sidebar'
                  }
                  onClick={() =>
                    isMobile
                      ? setDrawerOpen(true)
                      : setCollapsed((value) => !value)
                  }
                >
                  <Icon
                    icon={
                      isMobile
                        ? 'Hamburger'
                        : collapsed
                        ? 'ChevronRight'
                        : 'ChevronLeft'
                    }
                    color="currentColor"
                    size={20}
                  />
                </SidebarToggle>
              </Tooltip>
            }
          />
        </HeaderWrapper>
        <Content key={match?.path}>
          {isPermissionLoading ? null : hasCurrentPageAccess ? (
            children
          ) : (
            <NoAccessWrapper>
              <Text>No access</Text>
            </NoAccessWrapper>
          )}
        </Content>
      </Workspace>
      {isMobile && (
        <SiderDrawer
          placement="left"
          width="min(300px, 90vw)"
          title={t(`${pageLocale}.menus.implement-setting`)}
          onClose={() => setDrawerOpen(false)}
          visible={drawerOpen}
        >
          <div id="admin-sidebar-drawer">{renderSider()}</div>
        </SiderDrawer>
      )}
    </Wrapper>
  )
}

export default withConfigTheme(ManagementLayout)
